import React from 'react'
import NextImage from 'next/image'
import classNames from 'classnames'
import {
  DEFAULT_LINE_ITEM_IMAGE,
  NO_SHOW_ATTRIBUTES_PROPS
} from '@consts/index'
import { getLink } from '@helpers/env'
import { resizeShopifyImage } from '@helpers/shopify'
import ProducTitle from '@components/ui/ProducTitle'
import CartItemOption from '../CartItemOption'
import styles from './styles.module.scss'
import { useRouter } from 'next/router'
import FulfillmentInfo, {
  getFulfillmentConfig
} from '@components/product/mto/FulfillmentInfo'
import { CartLineItem } from 'types/checkout'
import { isMtoProduct } from '@components/product/mto/helpers'

const imageLoader = ({ src }: { src: string }) => {
  return `${src}&q=75`
}

export type Layout = 'small' | 'large' | undefined

type CartItemProps = {
  image?: string
  productTitle: string
  productType: string
  productTags?: CartLineItem['merchandise']['product']['tags']
  sku?: CartLineItem['merchandise']['sku']
  properties?: {
    key: string
    value: string
  }[]
  url?: string
  discounts?: string[]
  layout: Layout
  isBundle?: boolean
  isNestedItem?: boolean
  createdAt?: string
  handleOnClick?: () => void
}

function CartItem({
  image,
  productTitle,
  productType,
  properties = [],
  url,
  sku,
  discounts = [],
  children,
  layout = 'large',
  isBundle = false,
  productTags,
  isNestedItem,
  createdAt,
  handleOnClick
}: React.PropsWithChildren<CartItemProps>) {
  const router = useRouter()
  const hideProductType = !/^(Gift Card)$/.test(productType)
  const isSmallLayout = layout === 'small'

  const productOverviewClassNames = classNames(styles.productOverview, {
    [styles.small]: isSmallLayout
  })

  const imageContainerClassNames = classNames(
    styles.productOverview__imageContainer,
    {
      [styles.productOverview__nestedImage]: isNestedItem
    }
  )

  const cartItemImage = image
    ? resizeShopifyImage(image, {
        width: '164',
        height: '248',
        crop: 'center'
      })
    : DEFAULT_LINE_ITEM_IMAGE

  const isMTO = isMtoProduct(productTags?.join(','))

  const fulfillment = getFulfillmentConfig({ isMTO, isBundle, sku })

  const clickOnThumb = () => {
    if (url) {
      router.push(getLink(url))
    }
    if (handleOnClick) {
      handleOnClick()
    }
  }

  return (
    <div className={productOverviewClassNames}>
      <div className={imageContainerClassNames}>
        <button onClick={clickOnThumb}>
          <NextImage
            className={`${styles.productOverview__image}`}
            src={cartItemImage}
            alt={productTitle}
            width={isNestedItem ? '62' : '82'}
            height={isNestedItem ? '100' : '128'}
            loader={imageLoader}
          />
        </button>
      </div>
      <div className={`${styles.productOverview__content__cart}`}>
        <div className={styles.productOverview__details}>
          <h3 className={styles.productOverview__title}>
            <ProducTitle text={productType} />
          </h3>

          {hideProductType && (
            <p
              className={`${styles.productOverview__colour} ${styles.productOverview__line}`}
            >
              {productTitle}
            </p>
          )}
          <p
            className={`${styles.productOverview__sizes} ${styles.productOverview__line}`}
          >
            {properties
              .filter(attr => {
                return !NO_SHOW_ATTRIBUTES_PROPS.includes(attr.key)
              })
              .map(({ key, value }, index) => (
                <CartItemOption
                  key={`${productTitle}-${key}`}
                  value={value}
                  property={key}
                  newLine={(index + 1) % 3 === 0}
                />
              ))}
          </p>
          {discounts.map(discount => (
            <p
              key={discount}
              className={`${styles.productOverview__line} ${styles.productOverview__discount}`}
            >
              {discount}
            </p>
          ))}
        </div>
        {children}

        {fulfillment && (
          <FulfillmentInfo
            createdAt={createdAt}
            classes={styles.productOverview__mtoPara}
            fulfillment={fulfillment}
          />
        )}
      </div>
    </div>
  )
}

export default CartItem
